import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createQueue, getOneQueue } from '../../../API/repositories/queue';
import Form from '../../../common/components/Form';
import Input from '../../../common/components/Input';
import SelectInput from '../../../common/components/SelectInput';
import { useRequestsContext } from '../../../common/hooks/requestHook';
import Loading from '../../../common/components/Loading';
import { useCommonDataContext } from '../../../common/hooks/commonDataContext';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import SubmitButton from '@/common/components/buttons/SubmitButton';
import { QUEUE_STATUSES } from '@/pages/admin/queue/constants';

const QueueNew = ({ close, selectedToDuplicate }) => {
	const nameRef = useRef();
	const t1Ref = useRef();
	const t2Ref = useRef();
	const minutesBetweenCallsRef = useRef();
	const increaseTimeBetweenCallsRef = useRef();
	const increaseTimeBetweenCallsAfterRef = useRef();
	const firstDequeueAfterRef = useRef();
	const maxCallTimesRef = useRef();
	const maxKeepTimeRef = useRef();
	const shiftForConsultantKeepTimeRef = useRef();
	const selectWeightRef = useRef();
	const callWeightRef = useRef();
	const dequeuNotificationLimitRef = useRef();

	const [consultants, setConsultants] = useState();
	const [dataLoaded, setDataLoaded] = useState(false);

	const [selectedProductOption, setSelectedProductOption] = useState(null);
	const [selectedMarketOption, setSelectedMarketOption] = useState(null);
	const [selectedConsultantsOption, setSelectedConsultantsOption] = useState(
		[],
	);
	const [selectedQueueStatusesOption, setSelectedQueueStatusesOption] =
		useState(null);

	const { filterUsersByRoles, commonData, options } = useCommonDataContext();
	const { addMessage } = useMessageQueueContext();
	const { users } = commonData;
	const { marketsOptions, productsOptions } = options;
	const { hasUnfilledRequest, makeRequest } = useRequestsContext();

	const hadnleGetData = async () => {
		setConsultants(
			filterUsersByRoles('CALL_CENTER_OPERATOR', 'CALL_CENTER_MANAGER'),
		);
		setDataLoaded(true);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		const queue = {};
		queue.name = nameRef.current.value;
		queue.product = selectedProductOption.value;
		queue.market = selectedMarketOption.value;
		queue.consultants = selectedConsultantsOption.map(
			(consultant) => consultant.value,
		);
		queue.allowed_contact_statuses = selectedQueueStatusesOption.map(
			(status) => status.value,
		);
		queue.t1_new = t1Ref.current.value;
		queue.t2_fresh = t2Ref.current.value;
		queue.minutes_between_calls = minutesBetweenCallsRef.current.value;
		queue.increase_time_between_calls =
			increaseTimeBetweenCallsRef.current.value;
		queue.increase_time_between_calls_after =
			increaseTimeBetweenCallsAfterRef.current.value;
		queue.first_dequeue_after = firstDequeueAfterRef.current.value;
		queue.maximum_call_times = maxCallTimesRef.current.value;
		queue.max_keep_time = maxKeepTimeRef.current.value;
		queue.shift_for_consultant_keep_time =
			shiftForConsultantKeepTimeRef.current.value;
		queue.select_weight = selectWeightRef.current.value;
		queue.call_weight = callWeightRef.current.value;
		queue.dequeue_notification_limit =
			dequeuNotificationLimitRef.current?.value;

		const response = await makeRequest(createQueue.bind(null, queue));

		if (response.data) {
			addMessage('Created', 'success');
			close();
		} else {
			addMessage("Couldn't create", 'error');
		}
	};

	const duplicateData = async () => {
		if (selectedToDuplicate && dataLoaded) {
			const response = await makeRequest(
				getOneQueue.bind(null, selectedToDuplicate),
			);
			const queueToDuplicate = response?.data;
			t1Ref.current.value = queueToDuplicate.t1_new;
			t2Ref.current.value = queueToDuplicate.t2_fresh;
			minutesBetweenCallsRef.current.value =
				queueToDuplicate.minutes_between_calls;
			increaseTimeBetweenCallsRef.current.value =
				queueToDuplicate.increase_time_between_calls;
			increaseTimeBetweenCallsAfterRef.current.value =
				queueToDuplicate.increase_time_between_calls_after;
			firstDequeueAfterRef.current.value = queueToDuplicate.first_dequeue_after;
			maxCallTimesRef.current.value = queueToDuplicate.maximum_call_times;
			maxKeepTimeRef.current.value = queueToDuplicate.max_keep_time;
			shiftForConsultantKeepTimeRef.current.value =
				queueToDuplicate.shift_for_consultant_keep_time;
			selectWeightRef.current.value = queueToDuplicate.select_weight;
			callWeightRef.current.value = queueToDuplicate.call_weight;
			dequeuNotificationLimitRef.current.value =
				queueToDuplicate.dequeue_notification_limit;
			const consultantsToCopy = consultants.filter((c) =>
				queueToDuplicate.consultants?.includes(c.value),
			);
			setSelectedConsultantsOption(consultantsToCopy);
			nameRef.current.value = queueToDuplicate.name;

			const statuses = QUEUE_STATUSES.filter((status) =>
				queueToDuplicate.allowed_contact_statuses?.includes(status.value),
			);
			setSelectedQueueStatusesOption(statuses);
			dequeuNotificationLimitRef.current.value =
				queueToDuplicate?.dequeue_notification_limit;

			const product = productsOptions.find(
				(p) => p.value === queueToDuplicate.product,
			);
			setSelectedProductOption(product);

			const market = marketsOptions.find(
				(m) => m.value === queueToDuplicate.market,
			);
			setSelectedMarketOption(market);
		}
	};

	useLayoutEffect(() => {
		hadnleGetData();
	}, [users]);

	useEffect(() => {
		duplicateData();
	}, [selectedToDuplicate, dataLoaded]);

	return (
		<>
			{hasUnfilledRequest(createQueue) && <Loading />}
			<h3>Create Queue</h3>
			{consultants && marketsOptions && productsOptions && (
				<Form onSubmit={handleSubmit}>
					<div style={{ display: 'flex', gap: '20px' }}>
						<div>
							<Input
								inputRef={nameRef}
								name='Name'
								value=''
								required={true}
								disabled={false}
								width={100}
							/>
							<SelectInput
								name='Product'
								options={productsOptions}
								setSelected={setSelectedProductOption}
								selected={selectedProductOption}
								width={100}
								required
							/>
							<SelectInput
								name='Market'
								options={marketsOptions}
								setSelected={setSelectedMarketOption}
								selected={selectedMarketOption}
								width={100}
								required
							/>
							<SelectInput
								name='Consultants'
								options={consultants}
								setSelected={setSelectedConsultantsOption}
								selected={selectedConsultantsOption}
								width={100}
								multiple={true}
							/>
							<SelectInput
								name='Statuses'
								options={QUEUE_STATUSES}
								setSelected={setSelectedQueueStatusesOption}
								selected={selectedQueueStatusesOption}
								width={100}
								multiple={true}
								required
							/>
							<Input
								inputRef={t1Ref}
								name='T1 - new '
								value=''
								required={true}
								disabled={false}
								width={100}
								type='number'
							/>
							<Input
								inputRef={t2Ref}
								name='T2 - fresh '
								value=''
								required={true}
								disabled={false}
								width={100}
								type='number'
							/>
						</div>
						<div>
							<Input
								inputRef={minutesBetweenCallsRef}
								name='Minutes between calls'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								inputRef={increaseTimeBetweenCallsRef}
								name='Increase Time Between Calls'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								inputRef={increaseTimeBetweenCallsAfterRef}
								name='Increase Time Between Calls After'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								inputRef={firstDequeueAfterRef}
								name='First Dequeue After'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								inputRef={maxCallTimesRef}
								name='Max Call Times'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								inputRef={maxKeepTimeRef}
								name='Max Keep time'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								inputRef={shiftForConsultantKeepTimeRef}
								name='Shift for consultant keep time'
								value=''
								required={true}
								disabled={false}
								type='number'
							/>
							<Input
								name='Select weight'
								value=''
								required={true}
								type='number'
								inputRef={selectWeightRef}
							/>
							<Input
								name='Call weight'
								value=''
								required
								type='number'
								inputRef={callWeightRef}
							/>
							<Input
								name='Dequeue notification limit'
								value=''
								required
								type='number'
								inputRef={dequeuNotificationLimitRef}
							/>
						</div>
					</div>
					<SubmitButton text='Save' />
				</Form>
			)}
		</>
	);
};

export default QueueNew;
